import { useMemo, useState } from 'react';

import {
  Assets,
  Box,
  Button,
  Checkbox,
  Collapsable,
  CssGrid,
  Flex,
  Headings,
  Text,
  UseTextInputFieldReturnType,
} from '@foradevelopment/fora-ui';

import { useTypedTranslation } from '../../i18n/i18n';

type SearchFilterProps = {
  removePadding?: boolean;
  /** Checkboxes that will be shown in the table view */
  checkboxes: { text: string; props: { checked: boolean; onChange: () => void } }[];
  /** Related input fields that should be connected to the filter view. Mostly used to reset the filter */
  relatedSearchInputFields?: UseTextInputFieldReturnType[];
  totalAmount: number;
  amountShowing: number;
};

export const SearchFilter = ({
  checkboxes,
  removePadding,
  relatedSearchInputFields,
  amountShowing,
  totalAmount,
}: SearchFilterProps) => {
  const { t } = useTypedTranslation();
  const [hasActiveFiltersAtInitialRender] = useState(checkboxes.some(checkbox => checkbox.props.checked));
  const [isFilterExpanded, setIsFilterExpanded] = useState(hasActiveFiltersAtInitialRender);

  const hasActiveFilter = useMemo(() => {
    return (
      checkboxes.some(checkbox => checkbox.props.checked) || relatedSearchInputFields?.some(input => input.props.value)
    );
  }, [checkboxes, relatedSearchInputFields]);

  const handleClearFilter = () => {
    relatedSearchInputFields?.forEach(input => input.setValue(''));
    checkboxes.forEach(checkbox => checkbox.props.checked && checkbox.props.onChange());
  };

  return (
    <>
      <Collapsable
        expandedContent={
          <Box
            backgroundColor="white"
            marginBottom={20}
            {...(removePadding ? {} : { paddingBottom: 20, paddingLeft: 20, paddingRight: 20, paddingTop: 20 })}
          >
            <Flex justifyContent="space-between" marginBottom={20}>
              <Headings.H4>{t('common.filter.title')}</Headings.H4>
              <Box>
                <Button styleAs="link" onClick={() => setIsFilterExpanded(false)} testId="close-filter-button">
                  {t('common.close')} <Assets.MenuCloseIcon color="foraBlue" width={14} marginLeft={6} />
                </Button>
              </Box>
            </Flex>
            <CssGrid gridTemplateColumns={['repeat(1, 1fr)', 'repeat(3, 1fr)', 'repeat(4, 1fr)']} columnGap={10}>
              {checkboxes.map((checkbox, index) => (
                <Checkbox key={`checkbox-filter-${index}`} id={`checkbox-filter-${index}`} {...checkbox.props}>
                  {checkbox.text}
                </Checkbox>
              ))}
            </CssGrid>
          </Box>
        }
        isExpanded={isFilterExpanded}
      />

      <Flex columnGap={20} flexDirection="row" justifyContent="flex-start" alignItems="flex-end">
        <Flex flexDirection={['column', 'row']} columnGap={20} rowGap={[10]} alignItems="flex-start">
          {!isFilterExpanded ? (
            <Flex>
              <Button onClick={() => setIsFilterExpanded(true)} styleAs="link" testId="show-filter-button">
                {t('common.filter.showFilter')} <Assets.FilterIcon width={14} marginLeft={10} />
              </Button>
            </Flex>
          ) : null}
          {hasActiveFilter && (
            <Flex>
              <Text>
                {t('common.filter.foundEmployeesAmount-1', {
                  searchResultAmount: amountShowing,
                  totalRowsCount: totalAmount,
                })}
              </Text>
            </Flex>
          )}
        </Flex>
        {hasActiveFilter && (
          <Flex>
            <Button onClick={handleClearFilter} styleAs="link" color="burgundy" testId="clear-filter-button">
              <Assets.MenuCloseIcon marginTop={2} width={10} marginRight={10} color="burgundy" />
              {t('common.filter.clearFilterLink')}
            </Button>
          </Flex>
        )}
      </Flex>
    </>
  );
};
